// See EnrollHostsDrawer.spec.md for EHD-FR-005
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getBlueprints } from 'app/plugins/openstack/components/infrastructure/actions'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import Text from 'core/elements/Text'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
import { hasDnsRole, hasImageLibraryRole } from 'openstack/components/infrastructure/helpers'
import type { IResmgrHostSelector } from 'openstack/components/infrastructure/model'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'
import type { EnrollStepProps } from './EnrollHostsDrawer'
import StepSection from './StepSection'

// Exact text from EditServerRolesModal.tsx so the hint matches the old modal
const IMAGE_LIBRARY_TOOLTIP =
  'The image library role can be assigned to multiple hosts only if shared storage is enabled ' +
  'in the cluster blueprint. Remove the image library role from the other host and save the ' +
  'changes first.'
const DNS_TOOLTIP =
  'Only one host may have the dns role. Please deauthorize your existing dns host if you ' +
  'would like to assign a new dns.'

export interface RoleDisableReasonsInput {
  hosts: IResmgrHostSelector[]
  allHosts: IResmgrHostSelector[]
  imageLibrarySharedStorage: boolean
}

export interface RoleDisableReasons {
  imageLibrary?: string
  dns?: string
}

// EHD-FR-005: Image Library and DNS disable rules, reusing EditServerRolesModal's rules
// (isImageLibraryDisabled, enableRoleCheckbox for dns) against hosts outside this enrollment,
// since the hosts being enrolled here are new and never already hold either role.
export const roleDisableReasons = ({
  hosts,
  allHosts,
  imageLibrarySharedStorage,
}: RoleDisableReasonsInput): RoleDisableReasons => {
  const hostIds = new Set(hosts.map((host) => host.id))
  const otherHosts = allHosts.filter((host) => !hostIds.has(host.id))
  // TODO(design): pending Jay (PCD-9419 comment 230283, question 2) on whether enrolling 2+
  // hosts at once should block Image Library/DNS on its own
  const twoOrMoreHosts = hosts.length >= 2
  const reasons: RoleDisableReasons = {}

  if (!imageLibrarySharedStorage && (otherHosts.some(hasImageLibraryRole) || twoOrMoreHosts)) {
    reasons.imageLibrary = IMAGE_LIBRARY_TOOLTIP
  }
  if (otherHosts.some(hasDnsRole) || twoOrMoreHosts) {
    reasons.dns = DNS_TOOLTIP
  }
  return reasons
}

export default function RolesStep({ selection, onChange, hosts }: EnrollStepProps) {
  const classes = useStyles()
  const allHosts = useSelector(resmgrHostsSelector)
  const [imageLibrarySharedStorage, setImageLibrarySharedStorage] = useState(false)

  useEffect(() => {
    let active = true
    getBlueprints()
      .then((blueprints: Array<{ imageLibrarySharedStorage?: boolean }>) => {
        if (active) setImageLibrarySharedStorage(!!blueprints?.[0]?.imageLibrarySharedStorage)
      })
      .catch(() => {
        if (active) setImageLibrarySharedStorage(false)
      })
    return () => {
      active = false
    }
  }, [])

  const disableReasons = useMemo(
    () => roleDisableReasons({ hosts, allHosts, imageLibrarySharedStorage }),
    [hosts, allHosts, imageLibrarySharedStorage],
  )

  return (
    <div className={classes.root}>
      <StepSection
        title="Select Roles"
        description="Choose what these hosts do in the cluster. Selected roles are applied to every host in this enrollment."
      />
      <div className={clsx(classes.card, selection.imageLibrary && classes.cardSelected)}>
        <CheckboxField
          id="imageLibrary"
          label="VM Image Library"
          aria-label="VM Image Library"
          value={selection.imageLibrary}
          disabled={!!disableReasons.imageLibrary}
          info={disableReasons.imageLibrary}
          onChange={(value: boolean) => onChange({ imageLibrary: value })}
        />
        <Text variant="body2" className={classes.roleDescription}>
          Host serves and caches VM images for this cluster.
        </Text>
      </div>
      <div className={clsx(classes.card, selection.remoteSupport && classes.cardSelected)}>
        <CheckboxField
          id="remoteSupport"
          label="Advanced Remote Support"
          aria-label="Advanced Remote Support"
          value={selection.remoteSupport}
          onChange={(value: boolean) => onChange({ remoteSupport: value })}
        />
        <Text variant="body2" className={classes.roleDescription}>
          Outbound tunnel for Platform9 support diagnostics and remote assistance.
        </Text>
      </div>
      <div className={clsx(classes.card, selection.dns && classes.cardSelected)}>
        <CheckboxField
          id="dns"
          label="DNS"
          aria-label="DNS"
          value={selection.dns}
          disabled={!!disableReasons.dns}
          info={disableReasons.dns}
          onChange={(value: boolean) => onChange({ dns: value })}
        />
        <Text variant="body2" className={classes.roleDescription}>
          Host runs the DNS service for tenant networks.
        </Text>
      </div>
      {selection.hypervisor && (
        <>
          <div className={classes.earlierDivider}>
            <Text variant="body2" component="span" className={classes.earlierLabel}>
              Selected earlier
            </Text>
          </div>
          <div className={clsx(classes.card, classes.cardEarlier)}>
            <CheckboxField
              id="hypervisor-selected-earlier"
              label="Hypervisor"
              aria-label="Hypervisor"
              value={true}
              disabled
              // Always disabled, so CheckboxField never calls this; kept only to satisfy its
              // required onChange prop type. Not unit tested: there is no way to trigger it.
              onChange={() => undefined}
            />
            <Text variant="body2" className={classes.roleDescription}>
              Host runs virtual machines for this cluster.
            </Text>
          </div>
        </>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  // Same card look as the volume backend cards on step 3
  card: {
    border: `1px solid ${colors.grey[200]}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2, 3),
    // Figma: bold dark role name, also when the checkbox is disabled
    '& label': {
      fontWeight: 600,
      color: colors.grey[700],
    },
  },
  cardSelected: {
    borderColor: colors.blue[700],
    backgroundColor: colors.blue[100],
  },
  cardEarlier: {
    backgroundColor: colors.grey[50],
  },
  // Lined up under the role name, past the checkbox
  roleDescription: {
    color: colors.grey[700],
    paddingLeft: theme.spacing(4),
  },
  // Figma: "Selected earlier" centered between two lines
  earlierDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(1, 0),
    '&::before, &::after': {
      content: '""',
      flex: 1,
      borderTop: `1px solid ${colors.grey[200]}`,
    },
  },
  earlierLabel: {
    color: colors.grey[500],
  },
}))
